/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Col,
  Modal,
  notification,
  Row,
  Space,
  TablePaginationConfig,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { UploadOutlined, DeleteFilled } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { IStock, ISearchStockReq } from '@/interfaces/stock';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { getExcelData } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { importStock } from '@/adapters/stock';
import { twMerge } from 'tailwind-merge';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import dayjs from 'dayjs';
import { isEmpty, round } from 'lodash';

interface IProps {
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const _criteria: ISearchStockReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const ImportStockModal = ({ open, handleOk, handleCancel }: IProps) => {
  const [criteria, setCriteria] = useState<ISearchStockReq>({
    ..._criteria,
  });
  const [stock, setStock] = useState<IStock[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IStock> | SorterResult<IStock>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof IStock) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const columns: TableProps<IStock>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      fixed: 'left',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'Invoice No',
      dataIndex: 'ivNo',
      key: 'ivNo',
      className: 'font-bold',
      align: 'center',
      width: 100,
    },
    {
      title: 'Invoice Date',
      dataIndex: 'stockDate',
      key: 'stockDate',
      className: 'font-bold',
      align: 'center',
      width: 100,
      render: _ => formatDate(_),
    },

    {
      title: 'Item Code',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold',
      align: 'center',
      width: 140,
    },
    {
      title: 'Item Description',
      dataIndex: 'productName',
      key: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Variant',
      dataIndex: 'variant',
      key: 'variant',
      className: 'font-bold',
      width: 140,
    },
    {
      title: 'Lot',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      align: 'center',
      width: 120,
    },
    {
      title: 'Expire Date',
      dataIndex: 'coaExpiredDate',
      key: 'coaExpiredDate',
      className: 'font-bold',
      align: 'center',
      width: 120,
      render: _ => formatDate(_),
    },
    {
      title: 'Serial No.',
      dataIndex: 'serialNo',
      key: 'conversion',
      className: 'font-bold',
      width: 120,
    },
    {
      title: 'Qty',
      dataIndex: 'packQuantity',
      key: 'packQuantity',
      className: 'font-bold',
      align: 'right',
      width: 100,
      render: _ => formatNumber(_),
    },
    {
      title: 'UOM',
      dataIndex: 'baseUnitId',
      key: 'baseUnitId',
      className: 'font-bold',
      align: 'center',
      width: 100,
    },
    {
      title: 'Qty (KGS)',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'font-bold',
      align: 'right',
      width: 100,
      render: _ => formatNumber(_),
    },
    {
      title: 'Unit Price (USD)',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      className: 'font-bold text-blue-700',
      align: 'right',
      width: 120,
      render: _ => formatNumber(_),
    },
    {
      title: 'EXTENDED Amount (USD)',
      dataIndex: 'extendedAmount',
      key: 'extendedAmount',
      className: 'font-bold text-blue-700',
      align: 'right',
      width: 120,
      render: _ => formatNumber(_),
    },
    {
      title: 'Supplier Name',
      dataIndex: 'supplierName',
      key: 'supplierName',
      width: 350,
    },
    {
      title: 'Supplier Country',
      dataIndex: 'supplierCountry',
      key: 'supplierCountry',
      align: 'center',
      width: 100,
    },
    {
      title: 'Po No.',
      dataIndex: 'poNo',
      key: 'poNo',
      width: 120,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      align: 'center',
      width: 80,
      className: 'font-bold',
      render: _ => (
        <Space
          className={twMerge(
            _ === 2 && 'text-red-600',
            _ === 3 && 'text-green-700',
          )}
        >
          {_ === 1 ? 'Pending' : _ === 2 ? 'Error' : 'Success'}
        </Space>
      ),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => setStock(c => c.filter(x => x.code !== rec.code))}
        />
      ),
    },
  ];

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setStock([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      let excelData: any[] = (await getExcelData(file)) as any[];
      excelData = excelData
        .slice(1)
        .filter(c => !isEmpty(c[0]))
        .map((c: any) => {
          const stock: IStock = {
            ivNo: c[0],
            stockDate: dayjs(c[1]).toDate(),
            productCode: c[2],
            productName: c[3],
            variant: c[4] || '',
            code: c[5],
            coaExpiredDate: dayjs(c[6]).toDate(),
            serialNo: c[7] || '',
            packQuantity: Number(c[8] || 0),
            conversion: round(Number(c[10] || 0) / Number(c[8] || 0), 2),
            baseUnitId: c[9],
            baseUnitName: c[9],
            quantity: Number(c[10] || 0),
            unitPrice: Number(c[11] || 0),
            extendedAmount: Number(c[12] || 0),
            supplierName: c[13],
            supplierCountry: c[14],
            poNo: c[15] || '',
            status: 1,
            remark: c.remark?.toString() || '',
          } as IStock;
          return stock;
        });
      setStock(excelData);
      return false;
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const mutations = useMutation({
    mutationFn: async () => await importStock(stock),
    mutationKey: ['import-stock'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        setStock([]);
        notification.success({
          message: 'นำเข้าสต็อกสำเร็จ',
        });
        handleOk();
      } else {
        const duplicateStocks: string[] = data.data?.data;
        const updateStock = stock.map(c => {
          c.status = duplicateStocks.includes(c.code) ? 2 : 3;
          return c;
        });
        setStock(updateStock);
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onImportData = () => {
    mutations.mutateAsync();
  };

  return (
    <Modal
      title="นำเข้าสต็อก"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          onClick={() => onImportData()}
          loading={mutations.isPending}
        >
          นำเข้า
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-2 w-full">
        <Row gutter={12}>
          <Col span={24} md={8}>
            <Upload {...props}>
              {fileList.length === 0 && (
                <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
              )}
            </Upload>
          </Col>
        </Row>
        <TableComponent<IStock>
          columns={columns}
          criteria={criteria}
          data={{
            data: stock,
            totalRecords: stock.length,
          }}
          loading={false}
          onTableChange={onTableChange}
          // rowSelection={isMultiSelect ? rowSelection : undefined}
          scroll
          scrollLength={2300}
        />
      </div>
    </Modal>
  );
};

export default ImportStockModal;
